import React, { useEffect, useMemo, useState } from 'react'
import {
  DomainTable,
  DomainTableConatiner,
  FooterContainer,
  LoaderOverlay,
  SearchContainer,
  SelectDomainsWrapper,
} from '../Steps.styled'
import {
  ButtonWithIcon,
  LoadingIcon,
  InputWithIcon,
  Search,
} from '@clickcease/cc-client-components-lib'
import { useDomainsWithSearch } from 'Hooks/useDomainsWithSearch'
import {
  DOMAINS_TABLE_COLUMNS,
  DomainTableColumn,
  DomainToSelect,
} from '../../CrossSaleModal.types'
import { useTranslation } from 'react-i18next'
import { useAllowedLimits } from 'Hooks/useAllowedLimits'
import { DomainRow } from './DomainTableRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCirclePlus } from '@fortawesome/pro-light-svg-icons'
import { AddDomainRow } from './AddDomainRow'
import { useChangeByAccessor } from './useChangeByAccessor'
import { useUpdateDomainsStatus } from 'Features/domainsManagement/api/updateDomainsStatus'
import { useSetBzMonitorMode } from 'Features/domainsManagement/api/updateBzMonitorMode'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { TableHeader } from './TableHeader'

interface SelectDomainsProps {
  isBotMitigation: boolean
  onNextStep: (domains: DomainToSelect[]) => void
}

export const SelectDomains = ({ onNextStep, isBotMitigation }: SelectDomainsProps) => {
  const { t } = useTranslation('general')
  const { domains, isLoading: isLoadingDomains } = useDomainsWithSearch()
  const { domainsLeft } = useAllowedLimits()
  const { mutate: updateDomains, isLoading: isUpdatingDomains } = useUpdateDomainsStatus()
  const { mutateAsync: mutateAsyncIsMonitoring, isLoading: isSettingMonitoring } =
    useSetBzMonitorMode()
  const [domainsToSelect, setDomainsToSelect] = useState<DomainToSelect[]>([])
  const [searchText, setSearchText] = useState<string>('')
  const isLoading = isLoadingDomains || isUpdatingDomains || isSettingMonitoring
  const columns: DomainTableColumn[] = isBotMitigation
    ? DOMAINS_TABLE_COLUMNS
    : DOMAINS_TABLE_COLUMNS.filter((column) => column.accessor !== 'isBzMonitor')

  // mutate isMonitoring for all domains at once, in parallel
  const batchMutateIsMonitoring = (domains: DomainToSelect[], callBack: () => void) => {
    const promises = domains.map((domain) => {
      if (domain.isBzMonitor) {
        return mutateAsyncIsMonitoring({ isBzMonitor: domain.isBzMonitor, tagId: domain.tagId })
      }
    })

    Promise.all(promises)
      .then(() => callBack())
      .catch((err) => console.log('Error mutating isMonitoring for all domains', err))
  }

  const onNextStepClick = () => {
    const selectedDomains = domainsToSelect
      .filter((domain) => domain.isSelected)
      .sort((a, b) => Number(b.wordpress) - Number(a.wordpress))

    const domainsToActivate = selectedDomains.map((domain) => ({
      id: domain.id,
      wordpress: domain.wordpress,
      // Set product according to modal type - BM or PM
      ...(isBotMitigation ? { botZapping: true } : { paidMarketing: true }),
    }))

    updateDomains(domainsToActivate, {
      onSettled: (data) => {
        if (isBotMitigation && data && !(data instanceof Error)) {
          batchMutateIsMonitoring(selectedDomains, () => onNextStep(selectedDomains))
        } else if (!isBotMitigation && data && !(data instanceof Error)) {
          onNextStep(selectedDomains)
        }
      },
    })
  }

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const filteredDomains = useMemo<DomainToSelect[]>(() => {
    if (searchText.length > 0) {
      return domainsToSelect.filter((domain) => domain.address.includes(searchText.toLowerCase()))
    } else {
      return domainsToSelect
    }
  }, [domainsToSelect, searchText])

  const isALlSelectedObject = useMemo(() => {
    return {
      allSelected: !isLoading && domainsToSelect.every((domain) => domain.isSelected),
      allWordPress: !isLoading && domainsToSelect.every((domain) => domain.wordpress),
      allMonitored: !isLoading && domainsToSelect.every((domain) => domain.isBzMonitor),
    }
  }, [domainsToSelect, isLoading])

  // LIMITS
  const atLeastOneDomainSelected = domainsToSelect.some((domain) => domain.isSelected)
  const selectedDomainsCount = domainsToSelect.filter(
    (domain) => domain.isSelected && !domain.isNewlyCreated
  ).length
  const selectedDomainsLimitReached = isBotMitigation
    ? selectedDomainsCount === domainsLeft.botZapping
    : selectedDomainsCount === domainsLeft.paidMarketing
  const selectedDomainsLimitExceeded = isBotMitigation
    ? selectedDomainsCount > domainsLeft.botZapping
    : selectedDomainsCount > domainsLeft.paidMarketing
  const moreDomainsThanAllowed = isBotMitigation
    ? domains.length > domainsLeft.botZapping
    : domains.length > domainsLeft.paidMarketing

  const {
    allSelectedByAccessor,
    changeAllByAccessor,
    onIsWordPressChange,
    onIsMonitoredChange,
    onDomainSelect,
    addDomainToSelect,
  } = useChangeByAccessor({ setDomainsToSelect, domainsToSelect, isALlSelectedObject })

  useEffect(() => {
    const filteredDomains: DomainToSelect[] = []

    domains.forEach((domain) => {
      // if domain is already selected, we use the existing domain object
      const existingDomain = domainsToSelect.find(
        (existingDomain) => existingDomain.id === domain.id
      )

      if (existingDomain) {
        filteredDomains.push(existingDomain)
      } else if (
        // depending on product type, we filter out domains that already have the product
        (isBotMitigation && !domain.botZapping) ||
        (!isBotMitigation && !domain.paidMarketing)
      ) {
        filteredDomains.push({
          id: domain.id,
          isBzMonitor: domain.isBzMonitor,
          wordpress: domain.wordpress || false,
          address: domain.address,
          isSelected: false,
          paidMarketingConnected: domain.paidMarketingConnected,
          botZappingConnected: domain.botZappingConnected,
          botZappingPluginActivated: domain.botZappingPluginActivated,
          tagHashCode: domain.tagHashCode,
          tagUrl: domain.tagUrl,
          tagId: domain.tagId,
        })
      }
    })
    // if we get new domains form the server, we want only add new one, and not override the existing ones because they already have the user selection
    setDomainsToSelect(filteredDomains)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains])

  return (
    <SelectDomainsWrapper>
      <DomainTableConatiner>
        {isLoading && (
          <LoaderOverlay>
            <LoadingIcon width={60} height={60} />
          </LoaderOverlay>
        )}
        <SearchContainer>
          <InputWithIcon
            css={{ width: '250px', height: '32px' }}
            value={searchText}
            placeholder={t('crossSaleModal.searchPlaceholder')}
            onChange={onSearchInputChange}
            iconLeft={<Search />}
          />
        </SearchContainer>
        <TableHeader
          columns={columns}
          allSelectedByAccessor={allSelectedByAccessor}
          changeAllByAccessor={changeAllByAccessor}
          selectedDomainsLimitReached={selectedDomainsLimitReached}
          moreDomainsThanAllowed={moreDomainsThanAllowed}
        />
        <DomainTable>
          {filteredDomains.map((domain) => (
            <DomainRow
              domain={domain}
              columns={columns}
              selectDomain={onDomainSelect(domain.id)}
              setIsMonitored={onIsMonitoredChange(domain.id)}
              setIsWordPress={onIsWordPressChange(domain.id)}
              isMonitoredDisabled={!domain.wordpress}
              isSelectionDisabled={selectedDomainsLimitReached}
              isNewlyCreated={domain.isNewlyCreated}
              key={domain.id}
            />
          ))}
          {!isLoading && (
            <AddDomainRow
              columns={columns}
              setDomainsToSelect={addDomainToSelect}
              isBotMitigation={isBotMitigation}
              isDisabled={selectedDomainsLimitReached}
            />
          )}
        </DomainTable>
      </DomainTableConatiner>
      <FooterContainer>
        <ButtonWithIcon
          text={t('crossSaleModal.nextStep')}
          disabled={selectedDomainsLimitExceeded || !atLeastOneDomainSelected}
          iconLeft={<FontAwesomeIcon icon={faCirclePlus as IconProp} />}
          onClick={onNextStepClick}
        />
      </FooterContainer>
    </SelectDomainsWrapper>
  )
}
